
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiPackage, 
  FiUsers, 
  FiShoppingBag, 
  FiTrendingUp, 
  FiArrowRight, 
  FiFileText,
  FiPlus,
  FiEye,
  FiSettings,
  FiHome
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { orderAPI, productAPI, supplierAPI } from '../../services/api';

const DashboardHome = () => {
  const navigate = useNavigate();
  const { isMainAdmin, isSubAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalSuppliers: 0,
    totalOrders: 0,
    totalSales: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Récupérer les données depuis l'API
      const [productsResponse, suppliersResponse, ordersResponse] = await Promise.all([
        productAPI.getAll(),
        isMainAdmin ? supplierAPI.getAll() : Promise.resolve({ data: { data: [] } }),
        orderAPI.getAll()
      ]);

      // Calculer le total des ventes
      const orders = ordersResponse.data?.data?.data || ordersResponse.data || [];
      let totalSales = 0;
      
      orders.forEach(order => {
        try {
          // Gérer différents formats de montant
          const amount = order.total_Amount || order.total_amount || 0;
          if (typeof amount === 'string') {
            const cleanAmount = parseFloat(
              amount.toString()
                .replace(/\s+/g, '')
                .replace(',', '.')
            );
            totalSales += isNaN(cleanAmount) ? 0 : cleanAmount;
          } else {
            totalSales += amount;
          }
        } catch (error) {
          console.warn('Erreur de traitement du montant:', order);
        }
      });

      setStats({
        totalProducts: productsResponse.data?.length || productsResponse.data?.data?.length || productsResponse.data?.data?.data?.length || 0,
        totalSuppliers: suppliersResponse?.data?.data?.data?.length || suppliersResponse.data?.data?.length || 0,
        totalOrders: orders.length || orders.data?.length || 0,
        totalSales: totalSales
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      // Fallback avec des valeurs par défaut
      setStats({
        totalProducts: 0,
        totalSuppliers: 0,
        totalOrders: 0,
        totalSales: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Formater les nombres pour l'affichage mobile
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  // Cartes de statistiques
  const statCards = [
    {
      title: 'Produits',
      value: formatNumber(stats.totalProducts),
      rawValue: stats.totalProducts,
      icon: FiPackage,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-600',
      link: '/dashboard/products',
      description: 'Produits disponibles'
    },
    ...(isMainAdmin ? [{
      title: 'Fournisseurs',
      value: formatNumber(stats.totalSuppliers),
      rawValue: stats.totalSuppliers,
      icon: FiUsers,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      iconColor: 'text-green-600',
      link: '/dashboard/suppliers',
      description: 'Partenaires'
    }] : []),
    {
      title: 'Commandes',
      value: formatNumber(stats.totalOrders),
      rawValue: stats.totalOrders,
      icon: FiFileText,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      iconColor: 'text-purple-600',
      link: '/dashboard/orders',
      description: 'Commandes totales'
    },
    ...(isMainAdmin ? [{
      title: 'Ventes Totales',
      value: `${stats.totalSales.toLocaleString()} FCFA`,
      rawValue: stats.totalSales,
      icon: FiTrendingUp,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      iconColor: 'text-orange-600',
      link: '/dashboard/statistics',
      description: 'Chiffre d\'affaires'
    }] : [])
  ];

  // Actions rapides
  const quickActions = [
    {
      title: 'Ajouter un produit',
      icon: FiPlus,
      color: 'bg-gradient-to-r from-orange-500 to-orange-600',
      hoverColor: 'hover:from-orange-600 hover:to-orange-700',
      link: '/dashboard/products/add',
      shortText: 'Produit'
    },
    ...(isMainAdmin ? [{
      title: 'Ajouter un fournisseur',
      icon: FiUsers,
      color: 'bg-gradient-to-r from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
      link: '/dashboard/suppliers/add',
      shortText: 'Fournisseur'
    }] : []),
    {
      title: 'Gérer les commandes',
      icon: FiSettings,
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      hoverColor: 'hover:from-purple-600 hover:to-purple-700',
      link: '/dashboard/orders',
      shortText: 'Commandes'
    },
    {
      title: 'Voir la boutique',
      icon: FiEye,
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
      link: '/',
      shortText: 'Boutique'
    },
    ...(isMainAdmin ? [{
      title: 'Statistiques',
      icon: FiTrendingUp,
      color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
      hoverColor: 'hover:from-indigo-600 hover:to-indigo-700',
      link: '/dashboard/statistics',
      shortText: 'Stats'
    }] : [])
  ];

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600 mb-4"></div>
        <p className="text-gray-500 text-sm">Chargement des statistiques...</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      {/* En-tête */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Tableau de bord</h1>
          <button
            onClick={() => navigate('/')}
            className="hidden md:flex items-center space-x-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
          >
            <FiHome />
            <span>Voir la boutique</span>
          </button>
        </div>
        <p className="text-gray-600 text-sm md:text-base">
          {isMainAdmin ? 'Bienvenue Admin Principal' : 'Bienvenue Sous-Admin'} | 
          <span className="text-orange-600 font-medium ml-2">
            {new Date().toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </span>
        </p>
      </div>

      {/* Statistiques principales */}
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-bold text-gray-800">Aperçu global</h2>
          <button
            onClick={fetchDashboardStats}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            Actualiser
          </button>
        </div>

        {/* Version mobile compacte */}
        <div className="block md:hidden">
          <div className="grid grid-cols-2 gap-3 mb-4">
            {statCards.map((card, index) => (
              <div
                key={index}
                onClick={() => navigate(card.link)}
                className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 group active:scale-[0.98] transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className={`${card.bgColor} p-2 rounded-lg`}>
                    <card.icon className={`text-lg ${card.iconColor}`} />
                  </div>
                  <FiArrowRight className={`text-gray-300 group-hover:${card.iconColor} transition-colors`} />
                </div>
                <p className="text-xs text-gray-500 font-medium mb-1">{card.title}</p>
                <p className="text-base font-bold text-gray-800 truncate">
                  {card.value}
                </p>
                {card.rawValue > 0 && (
                  <div className="mt-1">
                    <div className="w-full bg-gray-100 rounded-full h-1">
                      <div 
                        className={`${card.color} h-1 rounded-full`}
                        style={{ width: `${Math.min(100, (card.rawValue / 100) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Version desktop */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, index) => (
            <div
              key={index}
              onClick={() => navigate(card.link)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 group hover:shadow-md transition-all cursor-pointer"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${card.bgColor} p-3 rounded-xl`}>
                  <card.icon className={`text-2xl ${card.iconColor}`} />
                </div>
                <FiArrowRight className={`text-gray-300 group-hover:${card.iconColor} group-hover:translate-x-1 transition-all`} />
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{card.title}</h3>
              <p className="text-2xl font-bold text-gray-800 mb-2">{card.value}</p>
              <p className="text-xs text-gray-500">{card.description}</p>
              <div className={`${card.color} h-1 mt-4 rounded-full`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Actions rapides */}
      <div className="mb-6 md:mb-8">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">Actions rapides</h2>
        
        {/* Version mobile (boutons compacts) */}
        <div className="block md:hidden">
          <div className="grid grid-cols-3 gap-3">
            {quickActions.slice(0, 3).map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.link)}
                className={`flex flex-col items-center justify-center p-3 ${action.color} text-white rounded-xl active:scale-95 transition-all`}
              >
                <action.icon className="text-xl mb-2" />
                <span className="text-xs font-medium text-center">{action.shortText}</span>
              </button>
            ))}
          </div>
          
          {quickActions.length > 3 && (
            <div className="mt-3">
              <div className="grid grid-cols-2 gap-3">
                {quickActions.slice(3).map((action, index) => (
                  <button
                    key={index + 3}
                    onClick={() => navigate(action.link)}
                    className={`flex items-center justify-center p-3 ${action.color} text-white rounded-xl active:scale-95 transition-all`}
                  >
                    <action.icon className="mr-2" />
                    <span className="text-sm font-medium">{action.shortText}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Version desktop */}
        <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.link)}
              className={`flex items-center justify-center space-x-3 px-4 py-4 ${action.color} ${action.hoverColor} text-white rounded-xl hover:shadow-lg transition-all font-semibold`}
            >
              <action.icon />
              <span>{action.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Section résumé */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 md:p-6">
        <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">Résumé de l'activité</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                <FiPackage className="text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Produits en stock</p>
                <p className="text-xs text-gray-500">Gérez votre inventaire</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-800">{stats.totalProducts}</p>
              <button
                onClick={() => navigate('/dashboard/products')}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Voir tout →
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="bg-purple-100 p-2 rounded-lg mr-3">
                <FiFileText className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700">Commandes actives</p>
                <p className="text-xs text-gray-500">Suivez vos commandes</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-gray-800">{stats.totalOrders}</p>
              <button
                onClick={() => navigate('/dashboard/orders')}
                className="text-xs text-purple-600 hover:text-purple-700 font-medium"
              >
                Gérer →
              </button>
            </div>
          </div>

          {isMainAdmin && (
            <>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="bg-green-100 p-2 rounded-lg mr-3">
                    <FiUsers className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Fournisseurs</p>
                    <p className="text-xs text-gray-500">Gérez vos partenaires</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-800">{stats.totalSuppliers}</p>
                  <button
                    onClick={() => navigate('/dashboard/suppliers')}
                    className="text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Voir tout →
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="bg-orange-100 p-2 rounded-lg mr-3">
                    <FiTrendingUp className="text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Chiffre d'affaires</p>
                    <p className="text-xs text-gray-500">Total des ventes</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-800">
                    {stats.totalSales.toLocaleString()} FCFA
                  </p>
                  <button
                    onClick={() => navigate('/dashboard/statistics')}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                  >
                    Statistiques →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Message d'accueil */}
      <div className="mt-6 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-md p-5 md:p-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-bold mb-2">
              {isMainAdmin ? 'Administration complète' : 'Gestion simplifiée'}
            </h3>
            <p className="text-orange-100 text-sm md:text-base mb-4 md:mb-0">
              {isMainAdmin 
                ? 'Vous avez un contrôle total sur la boutique, les fournisseurs et les statistiques.'
                : 'Gérez les produits et commandes de manière efficace.'
              }
            </p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="bg-white text-orange-600 hover:bg-orange-50 px-5 py-2 rounded-lg font-semibold transition-colors whitespace-nowrap"
          >
            Visiter la boutique
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;